import React from 'react';

import { DragArea } from './DragArea';
import { ColorRange } from './ColorRange';
import { ColorInput } from './ColorInput';
import { ColorPalette } from './ColorPalette';
import { useLocalStorageState } from 'hooks/UseLocalStorage';
import { Vector } from './JRCInputColorRawProps';

import styled from 'styled-components';
import { colorimetry } from 'utils/utils.color';
import { useIntl } from 'react-intl';

export const CSSColorPickerWrapper = styled.div<{ width: number; height: number }>`
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
    position: relative;
    pointer-events: none;
    border-radius: 8px;
    overflow: hidden;
`;

export const CSSColorPickerBackground = styled.div<{ width: number; height: number; backgroundColor: string }>`
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
    background-color: ${(props) => props.backgroundColor};
`;

export const CSSColorPickerGrarientBackground = styled.div`
    pointer-events: none;
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, #000, transparent), linear-gradient(90deg, #fff, hsla(0, 0%, 100%, 0));
`;

export const CSSColorPickerPicker = styled.div<{ size: number; backgroundColor: string; top: number; left: number }>`
    width: ${(props) => props.size}px;
    height: ${(props) => props.size}px;
    pointer-events: none;
    position: absolute;
    margin-top: -${(props) => props.size / 2}px;
    margin-left: -${(props) => props.size / 2}px;
    border-width: 2px;
    border-color: ${(props) => props.theme.color.white};
    border-style: solid;
    border-radius: ${(props) => props.size}px;
    background-color: ${(props) => props.backgroundColor};
    top: ${(props) => props.top}px;
    left: ${(props) => props.left}px;
`;

type ColorPickerPaletteType = {
    name?: string;
    colors: string[];
};
type ColorPickerType = {
    initialColor: string;
    width: number;
    height: number;
    palettes?: ColorPickerPaletteType[];
    onChange: (color: string, trigger: boolean) => void;
};

function getBackgroundColor(color: string): string {
    const hue = colorimetry.hex(color).toHsl().hue();
    return colorimetry.hsl(hue, 100, 50).toHex().string();
}

function getPicker(color: string, width: number, height: number): Vector {
    const rgb = colorimetry.hex(color).toRgb().values();
    const hsb = colorimetry.rgb(rgb.r, rgb.g, rgb.b).toHsb().values();
    const x = (hsb[1] / 100) * width;
    const y = height - (hsb[2] / 100) * height;

    return { x, y };
}

const MAX_LAST_COLORS = 14;

export const ColorPicker = ({ initialColor, width = 400, height = 200, palettes, onChange }: ColorPickerType) => {
    const intl = useIntl();
    const [backgroundColor, setBackgroundColor] = React.useState(getBackgroundColor(initialColor));
    const [picker, setPicker] = React.useState<Vector | undefined>(getPicker(initialColor, width, height));
    const [color, setColor] = React.useState(initialColor);
    const [lastUsedColors, setLastUsedColors] = useLocalStorageState<string[]>('color_picker_last_color', []);
    const [colorPickerStorageLock, setColorPickerStorageLock] = useLocalStorageState<boolean>(
        'color_picker_last_color_locked',
        false,
    );

    const setLocalStorage = (color: string) => {
        if (!colorPickerStorageLock && Array.isArray(lastUsedColors) && lastUsedColors.indexOf(color) === -1) {
            setLastUsedColors([color, ...lastUsedColors].slice(0, MAX_LAST_COLORS));
        }
    };

    return picker !== undefined ? (
        <>
            <DragArea
                width={width}
                height={height}
                initialPosition={picker}
                onChange={(picker, mouseIsDown) => {
                    const brightness = 1 - picker.y / height;
                    const saturation = picker.x / width;
                    const hue = colorimetry.hex(backgroundColor).toHsl().hue();
                    const rgb = colorimetry.hsb(hue, saturation, brightness).toRgb().values();
                    const hex = colorimetry.rgb(rgb[0], rgb[1], rgb[2]).toHex().string();

                    setColor(hex);
                    setPicker(picker);
                    onChange(hex, !mouseIsDown);
                    if (!mouseIsDown) {
                        setLocalStorage(hex);
                    }
                }}>
                <CSSColorPickerWrapper width={width} height={height}>
                    <CSSColorPickerBackground width={width} height={height} backgroundColor={backgroundColor} />
                    <CSSColorPickerGrarientBackground />
                    <CSSColorPickerPicker size={18} backgroundColor={'transparent'} top={picker.y} left={picker.x} />
                </CSSColorPickerWrapper>
            </DragArea>
            <div style={{ marginTop: 12 }} />
            <ColorRange
                width={width}
                hue={colorimetry.hex(initialColor).toHsl().hue()}
                onChange={(backgroundColor, hue, mouseIsDown) => {
                    const hues = colorimetry.hex(color).toHsl().values();
                    const hex = colorimetry.hsl(hue, hues[1], hues[2]).toHex().string();

                    setColor(hex);
                    setBackgroundColor(backgroundColor);
                    onChange(hex, !mouseIsDown);
                }}
            />
            <ColorInput
                initialValue={color}
                onChange={(color) => {
                    if (colorimetry.isValid(color)) {
                        const hue = colorimetry.hex(color).toHsl().hue();
                        const hex = colorimetry.hsl(hue, 100, 50).toHex().string();
                        const rgb = colorimetry.hex(color).toRgb().values();
                        const hsb = colorimetry.rgb(rgb.r, rgb.g, rgb.b).toHsb().values();
                        const x = (hsb[1] / 100) * width;
                        const y = height - (hsb[2] / 100) * height;

                        setPicker({ x, y });
                        setColor(color);
                        setBackgroundColor(hex);
                        onChange(color, true);
                        setLocalStorage(color);
                    }
                }}
            />
            {lastUsedColors && Array.isArray(lastUsedColors) ? (
                <ColorPalette
                    palette={{ name: intl.formatMessage({ id: 'COLOR_PICKER_recent_colors' }), colors: lastUsedColors }}
                    onClick={(color) => {
                        const rgb = colorimetry.hex(color).toRgb().values();
                        const hue = colorimetry.hex(color).toHsl().hue();
                        const hsb = colorimetry.rgb(rgb.r, rgb.g, rgb.b).toHsb().values();
                        const x = (hsb[1] / 100) * width;
                        const y = height - (hsb[2] / 100) * height;

                        setPicker({ x, y });
                        setBackgroundColor(colorimetry.hsl(hue, 100, 50).toHex().string());
                        setColor(color);
                        onChange(color, true);
                    }}
                    isLocked={colorPickerStorageLock}
                    onChange={(locked) => {
                        setColorPickerStorageLock(locked);
                    }}
                />
            ) : (
                <></>
            )}
            {palettes &&
                palettes.map((palette, index) => {
                    return (
                        <ColorPalette
                            palette={palette}
                            isLocked={false}
                            onClick={(color) => {
                                const rgb = colorimetry.hex(color).toRgb().values();
                                const hue = colorimetry.hex(color).toHsl().hue();
                                const hsb = colorimetry.rgb(rgb.r, rgb.g, rgb.b).toHsb().values();
                                const x = (hsb[1] / 100) * width;
                                const y = height - (hsb[2] / 100) * height;

                                setPicker({ x, y });
                                setBackgroundColor(colorimetry.hsl(hue, 100, 50).toHex().string());
                                setColor(color);
                                onChange(color, true);
                            }}
                            key={index}
                        />
                    );
                })}
        </>
    ) : (
        <></>
    );
};
